import { api } from '../../shared/api/client'
import {
  normalizeMpVisualImportPage,
  normalizeSiteCardCampaign,
  normalizeSiteCardCampaigns,
  normalizeSiteCategoryPreview,
  normalizeSiteProductCard,
  normalizeSiteProductStats,
  normalizeSiteProductsPage,
  normalizeSiteRecordPreview,
  normalizeSiteConnectionResult,
  normalizeSiteRunDetails,
  normalizeSiteRuns,
  normalizeSiteSettings,
} from './model'
import type {
  MpVisualImportPage,
  SiteCardCampaign,
  SiteCardCampaignMode,
  SiteCardCampaignScope,
  SiteCardCampaignStarted,
  SiteCategoryPreview,
  SiteProduct,
  SiteProductCard,
  SiteProductFilters,
  SiteProductSort,
  SiteProductStats,
  SiteProductView,
  SiteRecordPreview,
  SiteConnectionResult,
  SiteExchangeMode,
  SiteExchangeStarted,
  SiteRun,
  SiteRunDetails,
  SiteSettings,
  SiteSettingsDraft,
} from './types'

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

/** Ответ гейтвея завёрнут дважды: { data: { data: … } } либо { data: … }. */
const unwrap = (response: unknown): unknown => {
  const outer = asRecord(response)
  const data = asRecord(outer.data)
  return data.data ?? outer.data
}

export const siteAPI = {
  async getCategoryPreview(categoryId: number): Promise<SiteCategoryPreview> {
    return normalizeSiteCategoryPreview(
      unwrap(await api.get(`/site/category-preview/${categoryId}`)),
    )
  },

  async getSettings(): Promise<SiteSettings> {
    return normalizeSiteSettings(unwrap(await api.get('/site/settings')))
  },

  /**
   * Сохранение настроек.
   *
   * `token` уходит на сервер ТОЛЬКО если человек его трогал: поле черновика
   * тогда определено. Иначе ключ вообще отсутствует в теле, и бэкенд оставляет
   * прежний токен — показать сохранённый секрет форме нечем, и «пустое поле»
   * не должно означать «сотри».
   */
  async saveSettings(draft: SiteSettingsDraft): Promise<SiteSettings> {
    const body: Record<string, unknown> = {
      siteUrl: draft.siteUrl,
      apiPath: draft.apiPath,
      username: draft.username,
      batchSize: draft.batchSize,
      exchangeEnabled: draft.exchangeEnabled,
      autoIncremental: draft.autoIncremental,
      fullSchedule: draft.fullSchedule,
    }
    if (draft.token !== undefined) body.token = draft.token
    return normalizeSiteSettings(unwrap(await api.put('/site/settings', body)))
  },

  async testConnection(): Promise<SiteConnectionResult> {
    return normalizeSiteConnectionResult(unwrap(await api.post('/site/test-connection', {})))
  },

  /**
   * Запуск выгрузки. Ответ немедленный — задача уходит в фон, прогресс
   * приходит по WebSocket задач. jobId создаёт бэкенд и снаружи не принимает.
   */
  async startExchange(
    mode: SiteExchangeMode,
    dryRun: boolean,
    articles: string[] = [],
  ): Promise<SiteExchangeStarted> {
    const body: Record<string, unknown> = { mode, dryRun }
    if (mode === 'selection') body.articles = articles
    const raw = asRecord(unwrap(await api.post('/site/exchange', body)))
    const jobId = String(raw.jobId ?? '')
    if (!jobId) throw new Error('Сервис не вернул идентификатор фоновой задачи')
    return {
      jobId,
      runId: String(raw.runId ?? ''),
      mode: (raw.mode as SiteExchangeMode) ?? mode,
      dryRun: raw.dryRun === true,
    }
  },

  async getRuns(limit = 20): Promise<SiteRun[]> {
    return normalizeSiteRuns(unwrap(await api.get(`/site/runs?limit=${limit}`)))
  },

  async getRun(runId: string): Promise<SiteRunDetails> {
    return normalizeSiteRunDetails(unwrap(await api.get(`/site/runs/${runId}`)))
  },

  async getProductsStats(): Promise<SiteProductStats> {
    return normalizeSiteProductStats(unwrap(await api.get('/site/products/stats')))
  },

  async getProducts(
    view: SiteProductView,
    filters: SiteProductFilters,
    page: { limit: number; offset: number },
    signal?: AbortSignal,
    sort?: SiteProductSort,
  ): Promise<{
    rows: SiteProduct[]
    total: number
    totalIsApproximate: boolean
    /** Только в режиме articles_resolve — подмножество переданных значений, которым нашлась строка */
    articles_matched?: string[]
  }> {
    const params = new URLSearchParams()
    params.set('view', view)
    params.set('limit', String(page.limit))
    params.set('offset', String(page.offset))
    if (filters.search.trim()) params.set('search', filters.search.trim())
    if (filters.articles.length) params.set('articles', filters.articles.join(','))
    if (filters.categories.length) params.set('categories', filters.categories.join(','))
    if (filters.designCategories.length)
      params.set('designCategories', filters.designCategories.join(','))
    if (filters.designTypes.length) params.set('designTypes', filters.designTypes.join(','))
    if (filters.visual) params.set('visual', filters.visual)
    if (filters.hasPrice) params.set('hasPrice', filters.hasPrice)
    if (filters.seoSource) params.set('seoSource', filters.seoSource)
    if (filters.priceFrom) params.set('priceFrom', filters.priceFrom)
    if (filters.priceTo) params.set('priceTo', filters.priceTo)
    if (filters.dateFrom) params.set('updatedFrom', filters.dateFrom)
    if (filters.dateTo) params.set('updatedTo', filters.dateTo)
    if (sort) {
      params.set('sortBy', sort.by)
      params.set('sortOrder', sort.order)
    }
    return normalizeSiteProductsPage(unwrap(await api.get(`/site/products?${params}`, { signal })))
  },

  /**
   * Проверка списка артикулов на сервере: тот же запрос списка товаров с
   * articles_resolve=1 — сервер игнорирует прочие фильтры и возвращает
   * подмножество переданных значений, для которых нашёлся товар.
   */
  async resolveArticles(values: string[]): Promise<string[]> {
    const params = new URLSearchParams()
    params.set('articles', values.join(','))
    params.set('articles_resolve', '1')
    params.set('limit', '1')
    const raw = asRecord(unwrap(await api.get(`/site/products?${params}`)))
    return Array.isArray(raw.articles_matched) ? (raw.articles_matched as string[]) : []
  },

  async getProductCard(article: string): Promise<SiteProductCard> {
    return normalizeSiteProductCard(
      unwrap(await api.get(`/site/products/${encodeURIComponent(article)}`)),
    )
  },

  async previewRecords(articles: string[]): Promise<SiteRecordPreview> {
    return normalizeSiteRecordPreview(unwrap(await api.post('/site/preview', { articles })))
  },

  /**
   * Запуск догоняющей кампании — заведения карточек либо рендера визуалов.
   *
   * На сервер уходят режим, вид и фильтры, а не список артикулов: выборка на
   * десятки тысяч строк, и обход её ведёт бэкенд с контрольной точкой. Ответ
   * немедленный — кампания идёт фоновой задачей.
   */
  async startCardCampaign(
    view: SiteProductView,
    filters: SiteProductFilters,
    mode: SiteCardCampaignMode = 'cards',
  ): Promise<SiteCardCampaignStarted> {
    const raw = asRecord(
      unwrap(
        await api.post('/site/card-campaigns', { mode, view, ...campaignFilterBody(filters) }),
      ),
    )
    const runId = String(raw.runId ?? '')
    if (!runId) throw new Error('Сервис не вернул идентификатор кампании')
    return {
      runId,
      jobId: String(raw.jobId ?? ''),
      total: Number(raw.total ?? 0),
    }
  },

  /**
   * Меню окна запуска: сколько товаров выборки в каждой категории PIM.
   * Считается на сервере той же выборкой, которой пойдёт кампания, — режим
   * задаёт, считать товары без карточки или без фото.
   */
  async getCardCampaignScope(mode: SiteCardCampaignMode = 'cards'): Promise<SiteCardCampaignScope> {
    // Свой таймаут: общий у клиента — тридцать минут «под долгие операции», и
    // запрос, ушедший в момент рестарта гейтвея, вешал окно запуска намертво.
    const raw = asRecord(
      unwrap(await api.get(`/site/card-campaigns/scope?mode=${mode}`, { timeout: 60_000 })),
    )
    const categories = (Array.isArray(raw.categories) ? raw.categories : [])
      .map((item) => asRecord(item))
      .map((item) => ({
        value: String(item.value ?? ''),
        label: String(item.label ?? item.value ?? ''),
        total: Number(item.total ?? 0),
        withPhoto: Number(item.withPhoto ?? item.with_photo ?? 0),
      }))
      .filter((item) => item.value)
    return {
      mode: String(raw.mode ?? mode) === 'visuals' ? 'visuals' : 'cards',
      categories,
      total: Number(raw.total ?? categories.reduce((sum, item) => sum + item.total, 0)),
      withPhoto: Number(
        raw.withPhoto ?? raw.with_photo ?? categories.reduce((sum, item) => sum + item.withPhoto, 0),
      ),
    }
  },

  async getCardCampaigns(
    limit = 20,
  ): Promise<{ campaigns: SiteCardCampaign[]; active: SiteCardCampaign | null; last: SiteCardCampaign | null }> {
    return normalizeSiteCardCampaigns(unwrap(await api.get(`/site/card-campaigns?limit=${limit}`)))
  },

  async stopCardCampaign(runId: string): Promise<SiteCardCampaign | null> {
    return normalizeSiteCardCampaign(
      unwrap(await api.post(`/site/card-campaigns/${encodeURIComponent(runId)}/stop`, {})),
    )
  },

  /** Продолжение с контрольной точки: выборка берётся из строки кампании. */
  async resumeCardCampaign(runId: string): Promise<SiteCardCampaignStarted> {
    const raw = asRecord(
      unwrap(await api.post(`/site/card-campaigns/${encodeURIComponent(runId)}/resume`, {})),
    )
    return {
      runId: String(raw.runId ?? runId),
      jobId: String(raw.jobId ?? ''),
      checkpointArticle: raw.checkpointArticle ? String(raw.checkpointArticle) : null,
    }
  },

  /**
   * Страница предпросмотра переноса визуалов с площадок.
   *
   * Выборка задаётся так же, как у кампании: отмеченные строки, если они есть,
   * иначе вид с фильтрами экрана. Страницы листаются курсором-артикулом —
   * выборка «всё, что на экране» это до сотни тысяч товаров.
   */
  async getVisualImportCandidates(
    view: SiteProductView,
    filters: SiteProductFilters,
    options: {
      selectedArticles?: string[]
      after?: string | null
      limit?: number
      priority?: string[]
      signal?: AbortSignal
    } = {},
  ): Promise<MpVisualImportPage> {
    const params = new URLSearchParams()
    params.set('view', view)
    params.set('limit', String(options.limit ?? 100))
    if (options.after) params.set('after', options.after)
    if (options.selectedArticles?.length) {
      params.set('selectedArticles', options.selectedArticles.join(','))
    }
    if (options.priority?.length) params.set('priority', options.priority.join(','))
    for (const [key, value] of Object.entries(campaignFilterBody(filters))) {
      params.set(key, Array.isArray(value) ? value.join(',') : String(value))
    }
    return normalizeMpVisualImportPage(
      unwrap(await api.get(`/site/visual-import/candidates?${params}`, { signal: options.signal })),
    )
  },

  /**
   * Запуск переноса. На сервер уходит выборка, исключения и точечный выбор
   * источника — по всем строкам его слать нечем: выборка бывает на десятки
   * тысяч товаров, а подстановку по умолчанию бэкенд повторяет сам.
   */
  async startVisualImport(
    view: SiteProductView,
    filters: SiteProductFilters,
    options: {
      selectedArticles?: string[]
      excluded?: string[]
      choices?: Record<string, string>
      priority?: string[]
      total?: number
    } = {},
  ): Promise<{ jobId: string; total: number }> {
    const body: Record<string, unknown> = { view, ...campaignFilterBody(filters) }
    if (options.selectedArticles?.length) body.selectedArticles = options.selectedArticles
    if (options.excluded?.length) body.excluded = options.excluded
    if (options.choices && Object.keys(options.choices).length) body.choices = options.choices
    if (options.priority?.length) body.priority = options.priority
    if (options.total !== undefined) body.total = options.total
    const raw = asRecord(unwrap(await api.post('/site/visual-import', body)))
    const jobId = String(raw.jobId ?? '')
    if (!jobId) throw new Error('Сервис не вернул идентификатор фоновой задачи')
    return { jobId, total: Number(raw.total ?? 0) }
  },
}

/**
 * Фильтры экрана в тело запроса кампании.
 *
 * Имена совпадают с параметрами `GET /site/products` — бэкенд разбирает и то,
 * и другое одним кодом, и выборка кампании обязана совпасть с той, что человек
 * видит на экране. Пустые поля не отправляются вовсе: пустая строка в фильтре
 * даты означала бы границу, а не её отсутствие.
 */
function campaignFilterBody(filters: SiteProductFilters): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  if (filters.search.trim()) body.search = filters.search.trim()
  if (filters.articles.length) body.articles = filters.articles
  if (filters.categories.length) body.categories = filters.categories
  if (filters.designCategories.length) body.designCategories = filters.designCategories
  if (filters.designTypes.length) body.designTypes = filters.designTypes
  if (filters.visual) body.visual = filters.visual
  if (filters.hasPrice) body.hasPrice = filters.hasPrice
  if (filters.seoSource) body.seoSource = filters.seoSource
  if (filters.priceFrom) body.priceFrom = filters.priceFrom
  if (filters.priceTo) body.priceTo = filters.priceTo
  if (filters.dateFrom) body.updatedFrom = filters.dateFrom
  if (filters.dateTo) body.updatedTo = filters.dateTo
  return body
}
